import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { ChartConstants } from '@constants/pages/chart-constants';
import { PollingRateKey, StaticTextWidgetPollingRate } from '@constants/pages/user-defined-page/static-text-widget-polling-rate';
import { UserDefinedWidgetType } from '@constants/pages/user-defined-page/user-defined-widget-type';
import { Signal } from '@models/dashboard/signal';
import { Gateway } from '@models/gateway/gateway';
import { Machine } from '@models/machine/machine';
import { ChartSignalEntry, Colors } from '@models/pages/user-defined-page/chart-signal-entry';
import { UserDefinedWidget } from '@models/pages/user-defined-page/user-defined-widget';
import { TranslateService } from '@ngx-translate/core';
import { MachineService } from '@services/administration/machine/machine.service';
import { ChartsConfigurationHelperService } from '@services/charts/helper/charts-configuration-helper.service';
import { ChartsHelperService } from '@services/charts/helper/charts-helper.service';
import { ElasticSearchService } from '@services/elastic-search/elastic-search.service';
import { NotificationService } from '@services/notification/notification.service';
import { DragulaService } from 'ng2-dragula';
import { Subscription } from 'rxjs';

@Component({
    selector: 'scitis-command-widget-configuration',
    templateUrl: './command-widget-configuration.component.html',
    styleUrls: ['./command-widget-configuration.component.scss'],
    standalone: false
})
export class CommandWidgetConfigurationComponent {
  @Input()
  userDefinedWidget: UserDefinedWidget = new UserDefinedWidget();

  @Input()
  machineSignals: any;

  @Input()
  machine: Machine = new Machine();

  @Input()
  isTemplateWidget: boolean = false;

  @Output()
  isConfigurationValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  hasGenericGatewaySelected: EventEmitter<boolean> = new EventEmitter<boolean>();

  allUsableSignals: Signal[] = [];
  storedData: string[] = [];
  dragAndDropSubscription = new Subscription();
  dragAndDropItems = 'SIGNALS';
  dragAndDropStaticItems = 'STATIC_DATA';
  selectedSignalIds: number[] = [];
  isLoadingStaticData: boolean = true;
  selectedStaticData: string[] = [];
  pollingRate: StaticTextWidgetPollingRate[] = [];
  pollingRatePlaceholder: string = '';
  staticDataPlaceholder: string = '';
  selectedPollingRate: PollingRateKey | undefined;
  genericGateways: Gateway[] = [];
  selectedGateway: Gateway | undefined = undefined;
  isLoadingGatways: boolean = false;
  filteredMachineSignals: Signal[] = [];
  readonly UserDefinedWidgetType = UserDefinedWidgetType;

  constructor(private chartsConfigurationHelperService: ChartsConfigurationHelperService,
              private chartsHelperService: ChartsHelperService,
              private dragulaService: DragulaService,
              private notification: NotificationService,
              private translate: TranslateService,
              private elasticSearchService: ElasticSearchService) {
    this.pollingRatePlaceholder = this.translate.instant('Select polling rate...');
    this.staticDataPlaceholder = this.translate.instant('Select static data...');
    this.useDragula(this.dragAndDropItems);
  }

  get isTextWidget(): boolean {
    return this.userDefinedWidget.widgetDetails.widgetType === UserDefinedWidgetType.TEXT;
  }

  get isPollingRateValid() {
    return this.userDefinedWidget.userDefinedChartDetails.rangeValue >= 0;
  }

  get isStaticDataValid() {
    return this.selectedStaticData && this.selectedStaticData.length > 0;
  }

  ngOnInit(): void {
    this.setPollingRates();
    this.setSelectedPollingRate();
    this.getGenericGateways();
    this.allUsableSignals = [...this.chartsConfigurationHelperService.filterMisconfiguredSignals(this.machineSignals)];
    this.emitConfigChanged();
    if (!this.userDefinedWidget.userDefinedChartDetails.signals) {
      this.userDefinedWidget.userDefinedChartDetails.signals = [];
    }
  }

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler() {
    this.dragulaService.destroy(this.dragAndDropItems);
    this.dragulaService.destroy(this.dragAndDropStaticItems);
  }

  onSignalSelected($event: Signal | Signal[]) {
    this.userDefinedWidget.userDefinedChartDetails.signals = [];
    if (!$event) {
      this.emitConfigChanged();
      return;
    }

    const signals: Signal[] = Array.isArray($event) ? $event : [$event];

    for (const signal of signals) {

      if (this.shouldShowTooManySignalWarning()) {
        continue;
      }

      const chartSignal: ChartSignalEntry = new ChartSignalEntry(signal);
      chartSignal.colors = new Colors();
      chartSignal.colors.main = this.chartsConfigurationHelperService.convertHexColorToNumber(signal.signalColor);
      this.userDefinedWidget.userDefinedChartDetails.signals = [...this.userDefinedWidget.userDefinedChartDetails.signals, chartSignal];

    }
    this.emitConfigChanged();
  }

  onSetPollingRate($event: StaticTextWidgetPollingRate) {
    if (!$event) {
      this.userDefinedWidget.userDefinedChartDetails.rangeValue = -1;
      this.emitConfigChanged();
      return;
    }

    this.userDefinedWidget.userDefinedChartDetails.rangeValue = $event.pollingRateInS;
    this.emitConfigChanged();
  }

  onRemoveSignal(signal: ChartSignalEntry) {
    this.userDefinedWidget.userDefinedChartDetails.signals.splice(
      this.userDefinedWidget.userDefinedChartDetails.signals.findIndex(s => this.isTemplateWidget ? s.signalName.toLowerCase() === signal.signalName.toLowerCase() : s.signalId === signal.signalId),
      1);
    this.emitConfigChanged();
  }

  onRemoveData(data: string) {
    this.userDefinedWidget.userDefinedChartDetails.staticData.splice(this.userDefinedWidget.userDefinedChartDetails.staticData.findIndex(a => a === data), 1);
    this.emitConfigChanged();
  }

  ngOnDestroy() {
    this.dragAndDropSubscription.unsubscribe();
  }

  getSignalWritableStatus(signal: ChartSignalEntry) {
    return this.allUsableSignals.find(machineSignal => machineSignal.id == signal.signalId)?.writable ? 'writable' : '';
  }

  getSignalUnit(signal: Signal | ChartSignalEntry) {
    return this.chartsHelperService.getSignalUnit(signal);
  }

  onGatewayChange(event: Gateway) {
    this.filteredMachineSignals = [];
    this.userDefinedWidget.userDefinedChartDetails.gatewayId = event?.id;
    this.filteredMachineSignals = this.allUsableSignals.filter((signal: Signal) => signal.table.endsWith('_' + event.name));
    this.selectedSignalIds = [];
    this.userDefinedWidget.userDefinedChartDetails.signals = [];
    this.hasGenericGatewaySelected.emit(true);
  }

  onClear() {
    this.filteredMachineSignals = [];
    this.selectedSignalIds = [];
    this.userDefinedWidget.userDefinedChartDetails.signals = [];
    this.hasGenericGatewaySelected.emit(false);
  }

  private getGenericGateways() {
    if (this.machine.id) {
      this.isLoadingGatways = true;
      this.elasticSearchService.getGenericGatewaysByMachineId(this.machine.id).subscribe((gateways) => {
        if (gateways.result.data.length == 0) return;
        this.genericGateways = gateways.result.data;
        this.selectedGateway = this.genericGateways.find((gateway: Gateway) => this.userDefinedWidget.userDefinedChartDetails.gatewayId == gateway.id);
        if (this.selectedGateway == undefined) {
          this.selectedGateway = this.genericGateways[0];
        }
        this.userDefinedWidget.userDefinedChartDetails.gatewayId = this.selectedGateway?.id;
        this.filteredMachineSignals = this.allUsableSignals.filter((signal: Signal) => signal.table.endsWith('_' + this.selectedGateway!.name));
        this.hasGenericGatewaySelected.emit(!!this.selectedGateway);
        this.isLoadingGatways = false;
      });
    }
  }

  private setSelectedPollingRate() {
    if (this.userDefinedWidget.userDefinedChartDetails.usingStaticData) {
      switch (this.userDefinedWidget.userDefinedChartDetails.rangeValue) {
        case 0:
          this.selectedPollingRate = PollingRateKey.NO_POLLING;
          break;
        case 14400:
          this.selectedPollingRate = PollingRateKey.FOUR_HOURS;
          break;
        case 28800:
          this.selectedPollingRate = PollingRateKey.EIGHT_HOURS;
          break;
        case 86400:
          this.selectedPollingRate = PollingRateKey.TWENTY_FOUR_HOURS;
          break;
      }
    }
  }

  private setPollingRate(pollingRateKey: PollingRateKey) {
    switch (pollingRateKey) {
      case PollingRateKey.FOUR_HOURS:
        const pollingRate4 = this.translate.instant('every 4 hours');
        return new StaticTextWidgetPollingRate(pollingRate4, 4 * 60 * 60, PollingRateKey.FOUR_HOURS);
      case PollingRateKey.EIGHT_HOURS:
        const pollingRate8 = this.translate.instant('every 8 hours');
        return new StaticTextWidgetPollingRate(pollingRate8, 8 * 60 * 60, PollingRateKey.EIGHT_HOURS);
      case PollingRateKey.TWENTY_FOUR_HOURS:
        const pollingRate24 = this.translate.instant('every 24 hours');
        return new StaticTextWidgetPollingRate(pollingRate24, 24 * 60 * 60, PollingRateKey.TWENTY_FOUR_HOURS);
      case PollingRateKey.NO_POLLING:
        const pollingRateNoPoll = this.translate.instant('upon refresh');
        return new StaticTextWidgetPollingRate(pollingRateNoPoll, 0, PollingRateKey.NO_POLLING);
    }
  }

  private setPollingRates() {
    const pollingRate4Hours = this.setPollingRate(PollingRateKey.FOUR_HOURS);
    const pollingRate8Hours = this.setPollingRate(PollingRateKey.EIGHT_HOURS);
    const pollingRate24Hours = this.setPollingRate(PollingRateKey.TWENTY_FOUR_HOURS);
    const pollingRateNoPolling = this.setPollingRate(PollingRateKey.NO_POLLING);

    this.pollingRate = [pollingRate4Hours, pollingRate8Hours, pollingRate24Hours, pollingRateNoPolling];
  }

  private shouldShowTooManySignalWarning(): boolean {
    if (!this.userDefinedWidget.userDefinedChartDetails.usingStaticData && this.userDefinedWidget.userDefinedChartDetails.signals.length < ChartConstants.MAX_NUMBER_OF_SIGNAL_ON_TEXT_WIDGET) {
      return false;
    }

    this.notification.error(this.translate.instant('There are too many selected signals'), null, 'CommandWidgetConfigurationComponent', true);
    return true;
  }

  private useDragula(dragulaName: string) {
    this.dragAndDropSubscription.add(this.dragulaService.drag(dragulaName)
        .subscribe(({el}) => {
          el.classList.remove('ex-moved');
        })
    );
    this.dragAndDropSubscription.add(this.dragulaService.drop(dragulaName)
        .subscribe(({el}) => {
          el.classList.add('ex-moved');
        })
    );
    this.dragAndDropSubscription.add(this.dragulaService.over(dragulaName)
        .subscribe(({container}) => {
          container.classList.add('ex-over');
        })
    );
    this.dragAndDropSubscription.add(this.dragulaService.out(dragulaName)
        .subscribe(({container}) => {
          container.classList.remove('ex-over');
        })
    );
  }

  private emitConfigChanged(): void {
    if (!this.userDefinedWidget.userDefinedChartDetails.usingStaticData) {
      this.emitLiveDataConfigChanged();
    }
  }

  private emitLiveDataConfigChanged() {
    if (!this.userDefinedWidget.userDefinedChartDetails.signals || this.userDefinedWidget.userDefinedChartDetails.signals.length === 0) {
      this.selectedSignalIds = [];
      this.isConfigurationValid.emit(false);
    } else {
      if (this.isTemplateWidget) {
        const selectedSignals = this.userDefinedWidget.userDefinedChartDetails.signals;
        const selectedSignalIds: number[] = this.chartsConfigurationHelperService.getSelectedSignalIds(selectedSignals, this.allUsableSignals);
        this.selectedSignalIds = [...selectedSignalIds];
      } else {
        this.selectedSignalIds = this.userDefinedWidget.userDefinedChartDetails.signals.map(s => s.signalId);
      }
      this.isConfigurationValid.emit(true);
    }
  }
}
